import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import "./Header.scss"

import mainLogoWhite from "../../Assets/images/main-logo-white.svg"
import ccIcon from "../../Assets/images/cc-icon.svg"
import tcIcon from "../../Assets/images/tc-icon.svg"
import hsIcon from "../../Assets/images/hs-icon.svg"

import { NAV_LINKS } from '../../Config/routes';
import { useHeaderStyle } from '../../Context/HeaderStyle/HeaderStyleContext';

const SCROLL_THRESHOLD = 10;

const Header = ({ onSignUpClick, onSignInClick }) => {
  const { variant } = useHeaderStyle();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const headerRef = useRef(null);
  const lastScrollY = useRef(window.scrollY);  // initialize to current position, not 0
  const rafScheduled = useRef(false);
  const mobileMenuOpenRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (rafScheduled.current) return;
      rafScheduled.current = true;
      requestAnimationFrame(() => {
        if (mobileMenuOpenRef.current) {
          lastScrollY.current = window.scrollY;
          rafScheduled.current = false;
          return;
        }
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;
        if (delta > SCROLL_THRESHOLD && currentY > 0) {
          setHeaderHidden(true);
        } else if (delta < -SCROLL_THRESHOLD) {
          setHeaderHidden(false);
        }
        lastScrollY.current = currentY;
        rafScheduled.current = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    mobileMenuOpenRef.current = mobileMenuOpen;
    if (mobileMenuOpen) setHeaderHidden(false);
  }, [mobileMenuOpen]);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleOutsideClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [mobileMenuOpen]);

  return (
    <header
      ref={headerRef}
      className={`header-top${mobileMenuOpen ? ' nav-open' : ''}${headerHidden ? ' header-hidden' : ''}${variant === 'solid' ? ' header-solid' : ''}`}
    >
      <div className="container header-container">
        <div className="ht-left">
          <Link to="/" className="ht-logo">
            <img src={mainLogoWhite} alt="HaulSync Logo" />
          </Link>
        </div>

        <nav className="ht-center">
          {NAV_LINKS.map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) => `nav-link-item${isActive ? ' active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="ht-right">
          <div className="dropdown h-drop">
            <button
              type="button"
              className="dropdown-toggle hd-btn-signup"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              SIGN UP
            </button>
            <ul className="dropdown-menu hd-menu hd-menu-signup">
              <div className="id-inner">
                <ul>
                  <li>
                    <button type="button" onClick={() => onSignUpClick('cc')}>
                      <img className="drop-icon" src={ccIcon} alt="" />
                      Construction Company
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => onSignUpClick('tc')}>
                      <img className="drop-icon" src={tcIcon} alt="" />
                      Truck Company
                    </button>
                  </li>
                </ul>
              </div>
            </ul>
          </div>

          <div className="dropdown h-drop">
            <button
              type="button"
              className="dropdown-toggle hd-btn-signin"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              SIGN IN
            </button>
            <ul className="dropdown-menu hd-menu hd-menu-signin">
              <div className="id-inner">
                <ul>
                  <li>
                    <button type="button" onClick={() => onSignInClick('cc')}>
                      <img className="drop-icon" src={ccIcon} alt="" />
                      Construction Company
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => onSignInClick('tc')}>
                      <img className="drop-icon" src={tcIcon} alt="" />
                      Truck Company
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => onSignInClick('hs')}>
                      <img className="drop-icon" src={hsIcon} alt="" />
                      HaulSync
                    </button>
                  </li>
                </ul>
              </div>
            </ul>
          </div>
        </div>

        <button
          className={`hamburger-btn${mobileMenuOpen ? ' is-open' : ''}`}
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-nav-panel">
          <nav className="mobile-nav-links">
            {NAV_LINKS.map(({ path, label }) => (
              <NavLink
                key={path}
                to={path}
                end={path === '/'}
                className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="mobile-nav-actions">
            <div className="dropdown">
              <button
                type="button"
                className="dropdown-toggle hd-btn-signup mobile-auth-btn"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                SIGN UP
              </button>
              <ul className="dropdown-menu hd-menu hd-menu-signup">
                <div className="id-inner">
                  <ul>
                    <li>
                      <button type="button" onClick={() => { setMobileMenuOpen(false); onSignUpClick('cc'); }}>
                        <img className="drop-icon" src={ccIcon} alt="" />
                        Construction Company
                      </button>
                    </li>
                    <li>
                      <button type="button" onClick={() => { setMobileMenuOpen(false); onSignUpClick('tc'); }}>
                        <img className="drop-icon" src={tcIcon} alt="" />
                        Truck Company
                      </button>
                    </li>
                  </ul>
                </div>
              </ul>
            </div>
            <div className="dropdown">
              <button
                type="button"
                className="dropdown-toggle hd-btn-signin mobile-auth-btn"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                SIGN IN
              </button>
              <ul className="dropdown-menu hd-menu hd-menu-signin">
                <div className="id-inner">
                  <ul>
                    <li>
                      <button type="button" onClick={() => { setMobileMenuOpen(false); onSignInClick('cc'); }}>
                        <img className="drop-icon" src={ccIcon} alt="" />
                        Construction Company
                      </button>
                    </li>
                    <li>
                      <button type="button" onClick={() => { setMobileMenuOpen(false); onSignInClick('tc'); }}>
                        <img className="drop-icon" src={tcIcon} alt="" />
                        Truck Company
                      </button>
                    </li>
                    <li>
                      <button type="button" onClick={() => { setMobileMenuOpen(false); onSignInClick('hs'); }}>
                        <img className="drop-icon" src={hsIcon} alt="" />
                        HaulSync
                      </button>
                    </li>
                  </ul>
                </div>
              </ul>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;

